import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { triggerHaptic } from '@/lib/haptics';

const BANNER_DISMISSED_KEY = 'parkrow_install_banner_dismissed';
const BANNER_DELAY_MS = 30000; // 30 seconds

export function SmartInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const { isInstallable, isInstalled, promptInstall } = useInstallPrompt();

  useEffect(() => {
    // Don't show if already installed or not installable
    if (isInstalled || !isInstallable) return;

    // Check if user has already dismissed the banner
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Don't show again for 7 days after dismissal
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Show banner after 30 seconds for new visitors
    const timer = setTimeout(() => {
      setShowBanner(true);
      triggerHaptic('light');
    }, BANNER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled]);

  const handleInstall = async () => {
    triggerHaptic('medium');
    const result = await promptInstall();
    if (result.success) {
      triggerHaptic('success');
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    triggerHaptic('light');
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="bg-gradient-to-r from-primary/95 to-secondary/95 backdrop-blur-lg border-t border-border/20 p-4 shadow-2xl">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div className="text-white">
              <p className="font-bold text-sm md:text-base">Install Park Row</p>
              <p className="text-xs md:text-sm text-white/80">Get the full app experience</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleInstall}
              size="sm"
              className="bg-white text-primary hover:bg-white/90 font-bold min-h-[48px] min-w-[48px] px-4"
            >
              <Download className="h-4 w-4 mr-2" />
              Install
            </Button>
            <button
              onClick={handleDismiss}
              className="p-3 text-white/80 hover:text-white transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
